import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import { useTriggerGmailSyncMutation } from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IUseGmailRefresh {
  refresh: () => Promise<void>;
  syncing: boolean;
}

/**
 * Shared "refresh mail" action: enqueues an on-demand Gmail backfill (latest
 * ~100 messages per mailbox) and toasts the outcome. Runs async via Celery, so
 * new mail appears once the worker finishes — the manager reopens the card in a
 * few seconds. Reused by the dashboard and the student communication tab.
 */
export const useGmailRefresh = (): IUseGmailRefresh => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [syncGmail, { loading: syncing }] = useTriggerGmailSyncMutation();

  const refresh = useCallback(async () => {
    let ok = false;
    try {
      const res = await syncGmail();
      ok = Boolean(res.data?.syncGmailNow?.success);
    } catch {
      ok = false;
    }
    addToast({
      data: {
        type: ok ? ToastTypes.success : ToastTypes.error,
        title: t(
          ok
            ? 'pages.communications.gmailSyncQueued'
            : 'pages.communications.gmailSyncFailed',
        ),
      },
      type: GlobalToastTypeNames.FakturowniaMatch,
    });
  }, [syncGmail, addToast, t]);

  return { refresh, syncing };
};
